/**
 * "+ Create": every create screen this person may open, grouped by app.
 *
 * The current app's actions come first, because that is almost always what
 * you meant. Each entry is an existing create route (createActions.js); the
 * list is empty — and the button hidden — for someone who may create nothing.
 */
import { useTranslation } from 'react-i18next';
import { Link } from '../routes/orgNavigation.jsx';
import { Icon } from '../components/Icon.jsx';
import { Popover } from '../components/Popover.jsx';
import { AppIcon } from './Brand.jsx';
import { APPS_BY_KEY, appName } from './apps.js';
import { createLabel } from './createActions.js';

function groupByApp(actions, currentKey) {
  const groups = new Map();
  for (const action of actions) {
    if (!groups.has(action.app)) groups.set(action.app, []);
    groups.get(action.app).push(action);
  }
  return [...groups.entries()].sort(([a], [b]) => (a === currentKey ? -1 : b === currentKey ? 1 : 0));
}

export function QuickCreateMenu({ actions, currentKey }) {
  const { t } = useTranslation('navigation');
  return (
    <div className="qc">
      <div className="popover-title">{t('common:actions.create')}</div>
      <div className="qc-groups">
        {groupByApp(actions, currentKey).map(([appKey, list]) => {
          const app = APPS_BY_KEY[appKey];
          return (
            <div className="qc-group" key={appKey}>
              <div className="qc-group-label">
                <AppIcon app={app} size="sm" />
                {appName(app, t)}
              </div>
              {list.map((action) => (
                <Link key={action.to} to={action.to} className="qc-item" role="menuitem">
                  <Icon name={action.icon} size={15} />
                  <span>{createLabel(action, t)}</span>
                </Link>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function QuickCreate({ actions, currentKey }) {
  const { t } = useTranslation('navigation');
  if (!actions.length) return null;

  return (
    <Popover
      label={t('common:actions.create')}
      role="menu"
      align="end"
      panelClassName="popover-wide"
      trigger={({ triggerProps, open }) => (
        <button type="button" className={`topbar-create ${open ? 'is-open' : ''}`} {...triggerProps} title={t('common:actions.create')}>
          <Icon name="plus" size={16} strokeWidth={2.1} />
          <span className="topbar-create-label">{t('common:actions.create')}</span>
        </button>
      )}
    >
      <QuickCreateMenu actions={actions} currentKey={currentKey} />
    </Popover>
  );
}
